import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BaseService } from '../base-service';
import { FeatureSettings } from './feature-settings';

@Injectable({
  providedIn: 'root',
})
export class FeatureSettingsService extends BaseService {
  private readonly defaultSettings: FeatureSettings = {
    fidelity: true,
    pose: true,
    financement: true,
    basketSend: {},
  };

  private loaded = false;
  private loadingPromise?: Promise<FeatureSettings>;
  private canAdministerPromise?: Promise<boolean>;

  public settings: FeatureSettings = { ...this.defaultSettings };

  public get apiUrl(): string {
    return environment.configurators2d.baseApi + 'FeatureSettings/';
  }

  constructor() {
    super();
  }

  public get isLoaded(): boolean {
    return this.loaded;
  }

  public get fidelityEnabled(): boolean {
    return this.loaded && this.settings.fidelity;
  }

  public get poseEnabled(): boolean {
    return this.loaded && this.settings.pose;
  }

  public get financementEnabled(): boolean {
    return this.loaded && this.settings.financement;
  }

  /**
   * Indique si l'envoi au panier est activé pour un configurateur donné.
   * @param configurator Code du configurateur (valeur de CONFIG_CONTEXT).
   */
  public isBasketSendEnabled(configurator: string): boolean {
    return this.loaded && !!this.settings.basketSend?.[configurator];
  }

  public async isFidelityEnabled(): Promise<boolean> {
    return (await this.getSettings()).fidelity;
  }

  public async isPoseEnabled(): Promise<boolean> {
    return (await this.getSettings()).pose;
  }

  public async isFinancementEnabled(): Promise<boolean> {
    return (await this.getSettings()).financement;
  }

  public async getSettings(): Promise<FeatureSettings> {
    if (!this.loaded) {
      this.loadingPromise ??= this.refresh();
      await this.loadingPromise;
    }

    return this.snapshot;
  }

  public get snapshot(): FeatureSettings {
    return {
      fidelity: this.fidelityEnabled,
      pose: this.poseEnabled,
      financement: this.financementEnabled,
      basketSend: this.loaded ? (this.settings.basketSend ?? {}) : {},
    };
  }

  public async refresh(): Promise<FeatureSettings> {
    try {
      this.settings = await this.get<FeatureSettings>(this.apiUrl);
    } catch {
      this.settings = { ...this.defaultSettings };
    } finally {
      this.loaded = true;
      this.loadingPromise = undefined;
    }

    return this.settings;
  }

  public async update(settings: FeatureSettings): Promise<FeatureSettings> {
    this.settings = await this.put<FeatureSettings>(this.apiUrl, settings);
    this.loaded = true;
    return this.settings;
  }

  public async canAdminister(): Promise<boolean> {
    this.canAdministerPromise ??= this.get<boolean>(this.apiUrl + 'CanAdminister').catch(() => false);
    return this.canAdministerPromise;
  }

  public resetCanAdminister(): void {
    this.canAdministerPromise = undefined;
  }
}
